import React, { useEffect, useRef } from "react";
import { Subject, of } from "rxjs";
import {
  catchError,
  map,
  tap,
  takeUntil,
  distinctUntilChanged,
  debounceTime,
} from "rxjs/operators";
import xstream from "state-x-stream";
import PropTypes from "prop-types";
import { select, brushX } from "d3";

const errorHandler = (e) => {
  console.error(e);
  return of(null);
};

function controller({ props$ }) {
  const destroy$ = new Subject();
  const { zoomService } = props$.value;

  const brushBehavior = brushX()
    .on("start", ({ sourceEvent: e }) => e && e.stopPropagation())
    .on("brush", ({ selection, sourceEvent }) => {
      if (!sourceEvent) return;
      const { scale } = zoomService.xScale$.value;
      const [x0, x1] = selection.map((x) => scale.invert(x));
      zoomService.setXVisDomain(x0, x1);
    });

  function updateEls(els) {
    const { brushEl, leftAreaEl, rightAreaEl } = els;

    const brush = select(brushEl);
    const leftArea = select(leftAreaEl);
    const rightArea = select(rightAreaEl);

    function calcSelectionPoints() {
      const xScale = zoomService.xScale$.value;
      const xVisScale = zoomService.xVisScale$.value;
      const x0 = xScale.scale(xVisScale.d1);
      const x1 = xScale.scale(xVisScale.d2);
      return x0 < x1 ? { p1: x0, p2: x1 } : { p1: x1, p2: x0 };
    }

    function positionHandles({ p1, p2 }) {
      const { r1, r2 } = zoomService.xScale$.value;
      const padding = Math.abs(p2 - p1) < 1 ? 1 : 0;
      const rightTransform = `translateX(${p2 + padding}px)`;
      const leftTranform = `translateX(${p1 - padding}px)`;
      leftArea.style("transform", `translateX(-${r1 + r2}px) ${leftTranform}`);
      rightArea.style("transform", rightTransform);
    }

    function compareEpsilon(e) {
      return (a, b) => {
        const p1Diff = Math.abs(a.p1 - b.p1);
        const p2Diff = Math.abs(a.p2 - b.p2);
        return p1Diff < e && p2Diff < e;
      };
    }

    function adjust() {
      const { value: xscale } = zoomService.xScale$;
      const x0 = [xscale.r1, 0];
      const x1 = [xscale.rangeDist + xscale.r1, 50];
      const extent = [x0, x1];
      const { p1, p2 } = calcSelectionPoints();

      brush.call(brushBehavior.extent(extent));
      brush.call(brushBehavior.move, [p1, p2]);
    }

    zoomService.xScale$
      .pipe(
        map(calcSelectionPoints),
        tap(positionHandles),
        debounceTime(700),
        tap(adjust),
        catchError(errorHandler),
        takeUntil(destroy$)
      )
      .subscribe();

    zoomService.xVisScale$
      .pipe(
        map(calcSelectionPoints),
        distinctUntilChanged(compareEpsilon(0.5)),
        tap(positionHandles),
        debounceTime(700),
        tap(adjust),
        catchError(errorHandler),
        takeUntil(destroy$)
      )
      .subscribe();
  }

  return { updateEls, destroyEl: () => destroy$.next() };
}

function Brush({ updateEls, destroyEl }) {
  const brushRef = useRef(null);
  const leftAreaRef = useRef(null);
  const rightAreaRef = useRef(null);
  const leftHandleRef = useRef(null);
  const rightHandleRef = useRef(null);

  useEffect(() => {
    const els = {
      leftAreaEl: leftAreaRef.current,
      rightAreaEl: rightAreaRef.current,
      leftHandleEl: leftHandleRef.current,
      rightHandleEl: rightHandleRef.current,
      brushEl: brushRef.current,
    };

    updateEls(els);
    return () => destroyEl();
  }, [updateEls, destroyEl]);

  return (
    <>
      <svg width="100%" height="100%">
        <g ref={leftAreaRef} className="tw-w-full">
          <rect
            className="tw-text-blueGray-400 tw-fill-current tw-opacity-20"
            x={0}
            y={0}
            width="100%"
            height={40}
            strokeWidth={0}
          />
          <rect
            className="tw-text-orange-400 tw-fill-current"
            y={0}
            style={{ transform: "translateX(-4px)" }}
            x="100%"
            strokeWidth={0}
            height={10}
            width={4}
          />
        </g>
        <g ref={rightAreaRef}>
          <rect
            x={0}
            className="tw-text-blueGray-400 tw-fill-current tw-opacity-20"
            y={0}
            width="100%"
            height={40}
            strokeWidth={0}
          />
          <rect
            className="tw-text-orange-400 tw-fill-current"
            y={0}
            x={0}
            strokeWidth={0}
            height={10}
            width={4}
          />
        </g>
        <g ref={brushRef} width="100%" height="100%" className="tw-opacity-0" />
      </svg>
    </>
  );
}

Brush.propTypes = {
  updateEls: PropTypes.func.isRequired,
  destroyEl: PropTypes.func.isRequired,
};

export default xstream(controller)(Brush);
